import React, { useMemo } from 'react';
import { PrestationAvecPrix } from './types';

interface BestSupplierResultsTableProps {
  prestations: PrestationAvecPrix[];
  selectedSuppliers: string[];
  searchTerm: string;
  onQuantitesChange?: (quantites: Record<string, number>) => void;
}

const BestSupplierResultsTable: React.FC<BestSupplierResultsTableProps> = ({
  prestations,
  selectedSuppliers,
  searchTerm,
  onQuantitesChange,
}) => {
  const [hoveredTooltip, setHoveredTooltip] = React.useState<string | null>(null);
  // État pour stocker les quantités modifiées par l'utilisateur
  const [quantites, setQuantites] = React.useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    prestations.forEach((p) => {
      initial[p.id] = p.quantite;
    });
    return initial;
  });

  // Remonter les quantités initiales au parent au montage
  React.useEffect(() => {
    if (onQuantitesChange) {
      onQuantitesChange(quantites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mettre à jour les quantités quand les prestations changent (ajouter seulement les nouvelles)
  React.useEffect(() => {
    setQuantites((prev) => {
      const newQuantites: Record<string, number> = { ...prev };
      let hasChanges = false;
      prestations.forEach((p) => {
        if (!(p.id in newQuantites)) {
          newQuantites[p.id] = p.quantite;
          hasChanges = true;
        }
      });
      if (hasChanges && onQuantitesChange) {
        onQuantitesChange(newQuantites);
      }
      return newQuantites;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestations]);

  // Filtrer les prestations selon la recherche
  const filteredPrestations = useMemo(() => {
    if (!searchTerm) return prestations;
    const term = searchTerm.toLowerCase();
    return prestations.filter(
      (p) => p.intitule.toLowerCase().includes(term)
    );
  }, [prestations, searchTerm]);

  // Fonction pour formater les prix en français
  const formatPrix = (prix: number) => {
    return prix.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Gérer le changement de quantité
  const handleQuantiteChange = (prestationId: string, newQuantite: string) => {
    if (newQuantite === '') {
      // Si le champ est vide, garder 0 pour les calculs
      const newQuantites = {
        ...quantites,
        [prestationId]: 0,
      };
      setQuantites(newQuantites);
      onQuantitesChange?.(newQuantites);
    } else {
      const numValue = parseFloat(newQuantite);
      if (!isNaN(numValue) && numValue >= 0) {
        const newQuantites = {
          ...quantites,
          [prestationId]: numValue,
        };
        setQuantites(newQuantites);
        onQuantitesChange?.(newQuantites);
      }
    }
  };

  // Obtenir la quantité actuelle (modifiée ou originale)
  const getQuantite = (prestationId: string): number => {
    return quantites[prestationId] ?? 0;
  };

  // Calculer le prix total pour une prestation et un fournisseur
  const getPrixTotal = (prestation: PrestationAvecPrix, fournisseur: string): number | null => {
    const prixFournisseur = prestation.prixParFournisseur.find(
      (p) => p.fournisseur === fournisseur
    );
    if (!prixFournisseur) return null;
    const quantite = getQuantite(prestation.id);
    return quantite * prixFournisseur.prixUnitaire;
  };

  // Obtenir le prix unitaire pour une prestation et un fournisseur
  const getPrixUnitaire = (prestation: PrestationAvecPrix, fournisseur: string): number | null => {
    const prixFournisseur = prestation.prixParFournisseur.find(
      (p) => p.fournisseur === fournisseur
    );
    return prixFournisseur ? prixFournisseur.prixUnitaire : null;
  };

  // Calculer le total cumulé pour un fournisseur
  const getTotalFournisseur = (fournisseur: string): number => {
    return filteredPrestations.reduce((total, prestation) => {
      const prixTotal = getPrixTotal(prestation, fournisseur);
      return total + (prixTotal || 0);
    }, 0);
  };

  // Composant Tooltip
  const Tooltip: React.FC<{ text: string; children: React.ReactNode; id: string }> = ({
    text,
    children,
    id,
  }) => {
    const tooltipId = `${id}-tooltip`;
    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setHoveredTooltip(tooltipId)}
          onMouseLeave={() => setHoveredTooltip(null)}
        >
          {children}
        </div>
        {hoveredTooltip === tooltipId && (
          <div className="absolute z-50 w-48 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-normal">
            {text}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (filteredPrestations.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
        <div className="text-gray-400 text-lg mb-2">Aucune prestation trouvée</div>
        <div className="text-gray-500 text-sm">
          {searchTerm ? 'Essayez de modifier vos critères de recherche' : 'Aucune prestation disponible'}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden border border-gray-200 rounded-xl bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Colonne Prestations */}
                <th
                  scope="col"
                  className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r border-gray-200"
                >
                  Prestations
                </th>
                {/* Colonne Quantités */}
                <th
                  scope="col"
                  className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  Quantité
                </th>
                {/* Colonnes Fournisseurs */}
                {selectedSuppliers.map((fournisseur) => {
                  const total = getTotalFournisseur(fournisseur);
                  return (
                    <th
                      key={fournisseur}
                      scope="col"
                      className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200"
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-semibold text-violet-600">{fournisseur}</span>
                        <span className="mt-1 text-xs font-normal text-gray-500">
                          Total: {formatPrix(total)} €
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrestations.map((prestation) => (
                <tr key={prestation.id} className="hover:bg-gray-50 transition-colors">
                  {/* Cellule Prestation */}
                  <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-200">
                    <div className="text-sm font-medium text-gray-900">{prestation.intitule}</div>
                  </td>
                  {/* Cellule Quantité */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={getQuantite(prestation.id) || ''}
                        onChange={(e) => handleQuantiteChange(prestation.id, e.target.value)}
                        className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-500">{prestation.unite}</span>
                    </div>
                  </td>
                  {/* Cellules Fournisseurs */}
                  {selectedSuppliers.map((fournisseur) => {
                    const prixTotal = getPrixTotal(prestation, fournisseur);
                    const prixUnitaire = getPrixUnitaire(prestation, fournisseur);
                    const tooltipId = `${prestation.id}-${fournisseur}`;

                    return (
                      <td
                        key={fournisseur}
                        className="px-6 py-4 whitespace-nowrap text-center border-l border-gray-200"
                      >
                        {prixTotal !== null ? (
                          <Tooltip
                            id={tooltipId}
                            text={`Prix unitaire: ${formatPrix(prixUnitaire!)} €/${prestation.unite}`}
                          >
                            <div className="text-sm font-semibold text-violet-600 cursor-help">
                              {formatPrix(prixTotal)} €
                            </div>
                          </Tooltip>
                        ) : (
                          <div className="text-sm text-gray-400">-</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BestSupplierResultsTable;

